import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, resetState } from '../GlobalAction';
import {
  ActivityTypeEnum,
  ActivityTypeWithAllEnum,
  AdvanceActivityStepType,
} from '../../types/activity';

export enum AssetType {
  AUDIO = 'AUDIO',
  IMAGE = 'IMAGE',
  DOCUMENT = 'DOCUMENT',
  VIDEO = 'VIDEO',
  TEXT = 'TEXT',
}

enum AssignStatus {
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
}

enum UpdateActivityStatus {
  ACTIVATED = 'ACTIVATED',
  DEACTIVATED = 'DEACTIVATED',
  ERROR = 'ERROR',
}

export enum ActivityTemplateType {
  MAGIC_SUGGESTION = 'magic_suggestion',
  BLANK = 'blank',
  ACTIVITY_LIBRARY = 'activity_library',
}

export interface IActivityStep {
  id?: string;
  activityId?: string;
  description?: string;
  stepOrder: number;
  text: string;
  asset: string;
  // as default we will be IMAGE
  assetType?: AssetType;
  contentType?: string;
  // IMPORTANT: assetFile  store file object to persist step media asset
  // from this file local preview will be generated with URL.createObjectURL(assetFile)
  assetFile?: File | null;
  createdAt?: string;
  updatedAt?: string;

  isAdvanceStep?: boolean;
  advanceStepType?: AdvanceActivityStepType | '';
  advanceStepData?: string[];
}

export interface IAdvanceActivityTrackingSteps {
  id?: string;
  text: string;
}

interface IUserStep {
  id: string;
  activityId: string;
  currentStep: number;
  expiredAt: string;
  completedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface IStepMediaAsset {
  assetFile: File | null;
  assetType: string | null;
}

export interface IActivity {
  id?: string; // template
  clientId?: string;
  coachId?: string;
  name: string; // template
  type?: string; // template
  objective: string; // template
  coverImage?: string; // template
  schedule?: string;
  repeatOn?: number[];
  customMessage?: string | null;
  isCompleted?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  // template ActivitySteps like
  // {id, activityId, description, stepOrder, text, asset, assetType, createdAt, updatedAt} = IActivityStep )
  ActivityStep?: IActivityStep[];
  advanceActivityTrackingSteps?: IAdvanceActivityTrackingSteps[];
  UserActivity?: IUserStep[];
  expiredAt?: string;
  startAt?: string | Date;
  frequency?: ActivityTypeEnum;
  situation?: string;
  isAddToLibrary?: boolean;
  steps?: IActivityStep[];
  folderId?: string | null;
  isUserCreated?: boolean;
  isSharedWithCoach?: boolean;
}
export interface IAddActivityPayload {
  activityInput: IActivity;
  stepMediaAssets: IStepMediaAsset[];
  coachId: string;
}
export interface IFetchActivitiesPayload {
  clientId: string;
  coachId: string;
  skip: number;
  take: number;
  filterText: string;
  schedule?: ActivityTypeWithAllEnum;
}

export interface IUpdateActivityPayload {
  id: string;
  clientId: string;
  coachId: string;
  name?: string;
  type?: string;
  objective?: string;
  schedule?: string;
  repeatOn?: number[];
  customMessage?: string;
  description?: string;
  isCompleted?: boolean;
  nextCreate?: string;
  isActive?: boolean;
}

interface IActivityTemplate {
  type: ActivityTemplateType;
  activity: IActivity;
}

export interface IMagicSuggestion {
  title: string;
  summary: string;
  steps: string[];
  advance_activity_tracking_steps: string[];
  frequency: ActivityTypeEnum;
  situation: string;
}
interface ISuggestions {
  activities: IMagicSuggestion[];
}

export interface IGetMagicSuggestionsResponse {
  suggestions: ISuggestions;
  is_completed: boolean;
  id: string;
}

export interface IMagicSuggestionPayload {
  magicSuggestions: IGetMagicSuggestionsResponse;
}

interface IActivityState {
  suggestionState: 'CREATED' | 'CREATING' | 'REFRESH';
  newMagicSuggestionAvailable: boolean;
  magicSuggestions: IGetMagicSuggestionsResponse | null;
  selectedTemplate: IActivityTemplate | null;
  activities: IActivity[];
  selectedActivity: IActivity | null;
  totalActivityCount: number;
  draftActivity: IActivity | null;
  loading: boolean;
  error: null | string;
  assignStatus: AssignStatus | null;
  getMagicSuggestionLoading: boolean;
  magicSuggestionError: string | null;
  updateActivityLoading: boolean;
  updateActivityStatus: UpdateActivityStatus | null;
  activityExistenceStatus: boolean | null;
  // for selected client minimum one activity is created?
  isActivityCreated: boolean | null;
  fetchActivitiesLoading: boolean;
  addActivityLoading: boolean;
  isAddUpdateActivitySuccess: boolean;
  fetchTotalActivitiesCountLoading: boolean;
}

const initialState: IActivityState = {
  suggestionState: 'CREATED',
  newMagicSuggestionAvailable: true,
  magicSuggestions: null,
  selectedTemplate: null,
  // activities for selected client
  activities: [],
  // total activity count for selected client
  totalActivityCount: 0,
  selectedActivity: null,
  draftActivity: null,
  loading: true,
  error: null,
  assignStatus: null,
  getMagicSuggestionLoading: false,
  magicSuggestionError: null,
  updateActivityLoading: false,
  updateActivityStatus: null,
  activityExistenceStatus: null,
  isActivityCreated: null,
  fetchActivitiesLoading: false,
  addActivityLoading: false,
  isAddUpdateActivitySuccess: false,
  fetchTotalActivitiesCountLoading: false,
};

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    fetchMagicSuggestionsRequest: (
      state,
      _action: PayloadAction<{
        clientId: string;
        coachId: string;
      }>
    ) => {
      state.getMagicSuggestionLoading = true;
      state.magicSuggestions = null;
    },
    // for now we don't have type for magic suggestion
    fetchMagicSuggestionsSuccess: (state, _action: PayloadAction<IGetMagicSuggestionsResponse>) => {
      state.getMagicSuggestionLoading = false;
      state.magicSuggestions = _action.payload;
      if (state.magicSuggestions.is_completed) {
        state.suggestionState = 'CREATED';
      } else {
        state.suggestionState = 'CREATING';
      }
      state.magicSuggestionError = null;
      state.error = null;
    },
    updateMagicSuggesiosState: (
      state,
      action: PayloadAction<'CREATED' | 'CREATING' | 'REFRESH'>
    ) => {
      state.suggestionState = action.payload;
    },
    fetchMagicSuggestionsFailure: (state, action: PayloadAction<string>) => {
      state.getMagicSuggestionLoading = false;
      state.magicSuggestionError = action.payload;
    },
    fetchActivitiesRequest: (state, _action: PayloadAction<IFetchActivitiesPayload>) => {
      state.fetchActivitiesLoading = true;
    },
    fetchActivitiesSuccess: (
      state,
      action: PayloadAction<{
        activities: IActivity[];
        totalActivityCount: number;
        isActivityCreated: boolean;
      }>
    ) => {
      state.fetchActivitiesLoading = false;
      state.activities = action.payload.activities;
      state.error = null;
      state.totalActivityCount = action.payload.totalActivityCount;
      state.isActivityCreated = action.payload.isActivityCreated;
    },
    fetchActivitiesFailure: (state, action: PayloadAction<string>) => {
      state.fetchActivitiesLoading = false;
      state.error = action.payload;
    },
    fetchTotalActivitiesCountRequest: (state, _action: PayloadAction<{ clientId: string }>) => {
      state.fetchTotalActivitiesCountLoading = true;
    },
    fetchTotalActivitiesCountSuccess: (state, action: PayloadAction<number>) => {
      state.fetchTotalActivitiesCountLoading = false;
      state.error = null;
      state.totalActivityCount = action.payload;
    },
    fetchTotalActivitiesCountFailure: (state, action: PayloadAction<string>) => {
      state.fetchTotalActivitiesCountLoading = false;
      state.error = action.payload;
    },
    selectActivityTemplate: (state, action: PayloadAction<IActivityTemplate | undefined>) => {
      state.selectedTemplate = action.payload ?? null;
    },
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
    setSelectedActivityRequest: (state) => {
      state.loading = true;
    },
    setSelectedActivitySuccess: (state, action: PayloadAction<IActivity>) => {
      state.loading = false;
      state.selectedActivity = action.payload;
      state.error = null;
    },
    setSelectedActivityFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setDraftActivity: (state, action: PayloadAction<IActivity>) => {
      state.draftActivity = action.payload;
    },
    updateDraftActivity: (state, action: PayloadAction<IActivity>) => {
      state.draftActivity = action.payload;
    },
    clearDraftActivity: (state) => {
      state.draftActivity = null;
    },
    addActivityRequest: (state, _action: PayloadAction<IAddActivityPayload>) => {
      state.assignStatus = AssignStatus.SAVING;
      state.addActivityLoading = true;
    },
    addActivitySuccess: (state, action: PayloadAction<IActivity>) => {
      state.addActivityLoading = false;
      state.activities.push(action.payload);
      state.error = null;
      state.assignStatus = AssignStatus.SAVED;
      state.isAddUpdateActivitySuccess = true;
    },
    closeModalOnSuccess: (state) => {
      state.isAddUpdateActivitySuccess = false;
    },
    addActivityFailure: (state, action: PayloadAction<string>) => {
      state.addActivityLoading = false;
      state.error = action.payload;
      state.assignStatus = AssignStatus.ERROR;
    },
    toggleNewMagicSuggestionAvailable: (state, action: PayloadAction<boolean>) => {
      state.newMagicSuggestionAvailable = action.payload;
    },
    checkNewMagicSuggestionAvailableRequest: (state) => {
      state.loading = true;
    },
    resetAssignStatus: (state) => {
      state.assignStatus = null;
    },
    updateActivityRequest: (
      state,
      _action: PayloadAction<{ data: IUpdateActivityPayload; statusUpdate: boolean }>
    ) => {
      state.updateActivityLoading = true;
    },
    updateActivitySuccess: (state, action: PayloadAction<IActivity>) => {
      state.updateActivityLoading = false;
      state.error = null;
      const index = state.activities.findIndex((activity) => activity.id === action.payload.id);
      state.activities[index] = action.payload;
      state.isAddUpdateActivitySuccess = true;
    },
    updateActivityFailure: (state, action: PayloadAction<string>) => {
      state.updateActivityLoading = false;
      state.error = action.payload;
    },
    activityActivated: (state) => {
      state.updateActivityStatus = UpdateActivityStatus.ACTIVATED;
    },
    activityDeactivated: (state) => {
      state.updateActivityStatus = UpdateActivityStatus.DEACTIVATED;
    },
    resetUpdateActivityStatus: (state) => {
      state.updateActivityStatus = null;
    },
    checkActivityExistence: (state, _action: PayloadAction<string>) => {
      state.activityExistenceStatus = null;
    },
    checkActivityExistenceSuccess: (state, action: PayloadAction<boolean>) => {
      state.activityExistenceStatus = action.payload;
    },
    checkActivityExistenceFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.activityExistenceStatus = null;
    },
    resetCheckActivityExistenceStatus: (state) => {
      state.activityExistenceStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, () => initialState);
    builder.addCase(logout, () => initialState);
  },
});

export const {
  fetchMagicSuggestionsRequest,
  fetchMagicSuggestionsSuccess,
  fetchMagicSuggestionsFailure,
  fetchActivitiesRequest,
  fetchActivitiesSuccess,
  fetchActivitiesFailure,
  selectActivityTemplate,
  clearSelectedTemplate,
  setDraftActivity,
  addActivityRequest,
  addActivitySuccess,
  addActivityFailure,
  toggleNewMagicSuggestionAvailable,
  checkNewMagicSuggestionAvailableRequest,
  resetAssignStatus,
  updateActivityRequest,
  updateActivitySuccess,
  updateActivityFailure,
  activityActivated,
  activityDeactivated,
  resetUpdateActivityStatus,
  checkActivityExistence,
  checkActivityExistenceSuccess,
  checkActivityExistenceFailure,
  resetCheckActivityExistenceStatus,
  fetchTotalActivitiesCountRequest,
  fetchTotalActivitiesCountSuccess,
  updateMagicSuggesiosState,
  fetchTotalActivitiesCountFailure,
  closeModalOnSuccess,
  clearDraftActivity,
} = activitySlice.actions;

export default activitySlice.reducer;
